import { Component, OnInit, HostListener, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { firstValueFrom, filter } from 'rxjs';
import { AuthService } from '../../services/api.service';
import { BrnService } from '../../services/brn.service';
import { StoreRefreshService } from '../../services/store-refresh.service';
import { Bot } from '../../interfaces/bot.interface';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss']
})
export class SidebarComponent implements OnInit {
  userEmail: string = '';
  showAddStorePopup: boolean = false;
  storeName: string = '';
  isCreating: boolean = false;
  isMinified: boolean = false;

  // Dropdown functionality
  bots: Bot[] = [];
  currentBot: Bot | null = null;
  showDropdown: boolean = false;
  loadingBots: boolean = false;

  // User popup functionality
  showUserPopup: boolean = false;

  // Figma asset URLs (exact icons/logo from the selected frame)
  logoSrc: string = 'http://localhost:3845/assets/e136d46c88a72b32e4d88558ab21dcf469b9c29a.svg';
  companyChevronSrc: string = 'http://localhost:3845/assets/ec24316cef9fc7a723561a73c0e64a8eca537776.svg';
  companyAvatarCircleSrc: string = 'http://localhost:3845/assets/dbf2ff500271b5c5e5aa7c04a8eea89fa3634673.svg';
  pastConvIconA: string = 'http://localhost:3845/assets/0685f5223daaaad0bdecc6615c01ecd4551cb731.svg';
  pastConvIconB: string = 'http://localhost:3845/assets/5b7c38bde8158b595c269ebe5a977131ad8a4d9e.svg';
  pastConvIconC: string = 'http://localhost:3845/assets/f73867a419b2356b118d6e7962ab0d63f5b6033a.svg';
  settingsIconSrc: string = 'http://localhost:3845/assets/3b69642f770507ab3b365f0f81a1f99e81de6280.svg';
  brandsIconSrc: string = 'http://localhost:3845/assets/0a0fe0ea4b3f8f1bdbb597def27cfd0fde7a62f3.svg';
  userIconSrc: string = 'http://localhost:3845/assets/faa9b264f9b028a2ba686611c562fe3ab0a39766.svg';
  userChevronSrc: string = 'http://localhost:3845/assets/fb4b765e05dea971db84c49094de91ea91006274.svg';

  @Output() sidebarToggle = new EventEmitter<void>();

  onToggleSidebar() {
    this.isMinified = !this.isMinified;
    // Save sidebar state to localStorage
    localStorage.setItem('sidebar-minified', JSON.stringify(this.isMinified));
    this.sidebarToggle.emit();
  }

  constructor(
    private authService: AuthService,
    private brnService: BrnService,
    private storeRefreshService: StoreRefreshService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    const authData: any = localStorage.getItem('ngStorage-authData')
      ? JSON.parse(localStorage.getItem('ngStorage-authData') || '{}')
      : null;

    this.userEmail = authData?.email || authData?.user?.email || 'user@example.com';

    // Load saved sidebar state
    const savedSidebarState = localStorage.getItem('sidebar-minified');
    if (savedSidebarState !== null) {
      this.isMinified = JSON.parse(savedSidebarState);
    }

    // Load bots (which will also determine current bot)
    this.loadBots();

    // Subscribe to store refresh events to reload bots
    this.storeRefreshService.refreshStores$.subscribe(() => {
      this.loadBots();
    });

    // Listen to route changes to update current bot
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (this.bots.length > 0) {
        this.determineCurrentBot();
      }
    });
  }

  navigateToStores() {
    this.router.navigate(['/stores']);
  }

  toggleAddStore() {
    this.showAddStorePopup = !this.showAddStorePopup;
    if (this.showAddStorePopup) {
      this.storeName = '';
    }
  }

  navigateToChat(botId: string) {
    if (botId) {
      localStorage.setItem('current-bot-id', botId);
      this.router.navigate(['/chat', botId]);
    }
  }

  closeAddStorePopup() {
    this.showAddStorePopup = false;
    this.storeName = '';
  }

  async createBot() {
    if (this.storeName.trim() && !this.isCreating) {
      this.isCreating = true;
      try {
        const trimmedName = this.storeName.trim();
        const createResp = await firstValueFrom(this.brnService.createBot(trimmedName));

        let newBotId: string | undefined =
          (createResp as any)?.bot?.bot_id ||
          (createResp as any)?.bot_id ||
          (createResp as any)?.id ||
          (createResp as any)?.bot?.id;

        if (!newBotId) {
          for (let attempt = 1; attempt <= 5 && !newBotId; attempt++) {
            try {
              const botsResp = await firstValueFrom(this.authService.getBots(200, 0));
              const matched = botsResp?.bots?.find(
                (b: any) => (b?.name || '').trim().toLowerCase() === trimmedName.toLowerCase()
              );
              newBotId = matched?.bot_id;
              if (!newBotId) {
                await new Promise(res => setTimeout(res, 400));
              }
            } catch {
              await new Promise(res => setTimeout(res, 400));
            }
          }
        }

        if (newBotId) {
          try {
            await firstValueFrom(this.brnService.createScenario(newBotId));
          } catch { }
        }

        this.closeAddStorePopup();

        if (this.router.url !== '/stores') {
          this.router.navigate(['/stores']);
        } else {
          this.storeRefreshService.triggerRefresh();
        }
      } catch (error: any) {
        console.error('Error creating bot:', error);
      } finally {
        this.isCreating = false;
      }
    }
  }


  // Load bots for dropdown
  async loadBots() {
    this.loadingBots = true;
    try {
      const response = await firstValueFrom(this.authService.getBots(100, 0));
      if (response && response.bots) {
        this.bots = response.bots;
        // Determine current bot after loading bots
        this.determineCurrentBot();
      }
    } catch (error) {
      console.error('Error loading bots:', error);
    } finally {
      this.loadingBots = false;
    }
  }

  // Determine current bot based on route
  determineCurrentBot() {
    const url = this.router.url;
    const botId = this.extractBotIdFromUrl(url) ?? localStorage.getItem('current-bot-id');
    if (botId) {
      localStorage.setItem('current-bot-id', botId);
      this.currentBot = this.bots.find(bot => bot.bot_id === botId) || null;
    } else {
      this.currentBot = null;
    }
  }

  // Extract bot id from supported routes
  private extractBotIdFromUrl(url: string): string | null {
    const match = url.match(/\/(store|api-keys|reports|chat|chat-clients)\/([^\/\?]+)/);
    return match ? match[2] : null;
  }

  // Whether current URL has a bot context
  hasBotContext(): boolean {
    return this.extractBotIdFromUrl(this.router.url) !== null;
  }

  // Toggle dropdown
  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  // Select bot from dropdown
  selectBot(bot: Bot) {
    this.currentBot = bot;
    localStorage.setItem('current-bot-id', bot.bot_id);
    this.showDropdown = false;
    this.router.navigate(['/store', bot.bot_id]);
  }

  // Get bot initials for avatar
  getBotInitials(name: string): string {
    if (!name || name.trim().length === 0) {
      return '??';
    }

    const words = name.trim().split(/\s+/);

    if (words.length === 1) {
      const word = words[0];
      return word.length >= 2 ? word.substring(0, 2).toUpperCase() : (word + '?').substring(0, 2).toUpperCase();
    } else {
      const firstLetter = words[0].charAt(0);
      const secondLetter = words[1].charAt(0);
      return (firstLetter + secondLetter).toUpperCase();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const clicked = event.target as HTMLElement;

    // Handle add store popup
    if (this.showAddStorePopup) {
      const container = document.querySelector('.sidebar-add-store-container');
      if (container && !container.contains(clicked)) {
        this.closeAddStorePopup();
      }
    }

    // Handle dropdown
    if (this.showDropdown) {
      const dropdownContainer = document.querySelector('.company-dropdown-container');
      if (dropdownContainer && !dropdownContainer.contains(clicked)) {
        this.showDropdown = false;
      }
    }

    // Handle user popup
    if (this.showUserPopup) {
      const userContainer = document.querySelector('.sidebar-user');
      if (userContainer && !userContainer.contains(clicked)) {
        this.showUserPopup = false;
      }
    }
  }

  // Toggle user popup
  toggleUserPopup(): void {
    this.showUserPopup = !this.showUserPopup;
  }

  // Check if a nav item is active based on current route
  isNavItemActive(route: string): boolean {
    const currentUrl = this.router.url;

    if (route === 'settings') {
      // Settings is active for store, reports and api-keys routes
      return currentUrl.includes('/store/') || currentUrl.includes('/api-keys/') || currentUrl.includes('/reports/');
    } else if (route === 'stores') {
      // Stores is active for /stores route
      return currentUrl === '/stores';
    } else if (route === 'chat-clients') {
      return currentUrl.includes('/chat-clients/');
    }
    else if (route === 'chat') {
      return currentUrl.includes('/chat/');
    }

    return false;
  }

  // Navigate to settings (API keys for current bot)
  navigateToSettings() {
    const idFromUrl = this.extractBotIdFromUrl(this.router.url);
    const id = this.currentBot?.bot_id || idFromUrl;
    if (id) {
      this.router.navigate(['/api-keys', id]);
    }
  }

  // Navigate to chat clients (past conversations)
  navigateToChatClients() {
    const idFromUrl = this.extractBotIdFromUrl(this.router.url);
    const id = this.currentBot?.bot_id || idFromUrl;
    if (id) {
      this.router.navigate(['/chat-clients', id]);
    }
  }

  // Logout functionality
  logout(): void {
    this.showUserPopup = false;
    this.authService.logout();
  }
}


